import React, {
	useImperativeHandle,
	useCallback,
	useMemo,
	type KeyboardEvent,
	type ElementRef,
} from 'react';
import { styled } from '@compiled/react';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import { Box, xcss } from '@atlaskit/primitives';
import { N300A, N900 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueKey } from '@atlassian/jira-shared-types';
import type { CellNavigationProps } from '../../../common/types/cell-navigation';
import { CellWrapper } from '../../../common/ui/list-cell';
import messages from './messages';

type Props = {
	issueKey: IssueKey;
	onClick: (issueId: string) => void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	totalCount: number | void;
} & CellNavigationProps;

export const CommentsCell = ({
	issueKey,
	isSelected = false,
	onClick,
	wrapperRef,
	onFocus,
	onBlur,
	onKeyDown,
	totalCount,
	cellRef,
}: Props) => {
	const { formatMessage } = useIntl();

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const handleClick = useCallback(() => {
		onClick(issueKey);
	}, [onClick, issueKey]);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter') {
				handleClick();
				return;
			}
			onKeyDown?.(e);
		},
		[onKeyDown, handleClick],
	);

	const handleKeyDownNew = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (event.code === 'Enter') {
				onClick(issueKey);
				return true;
			}

			return false;
		},
		[issueKey, onClick],
	);

	const cellOverrides = useCallback(
		() => ({
			onKeyDown: handleKeyDownNew,
			onClick: () => true,
		}),
		[handleKeyDownNew],
	);
	useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const onWrapperRef = useCallback(
		(ref: ElementRef<'div'> | null) => {
			if (wrapperRef) {
				// eslint-disable-next-line no-param-reassign
				wrapperRef.current = ref;
			}
		},
		[wrapperRef],
	);

	const renderedCommentCount = useMemo(() => {
		if (totalCount == null) {
			return null;
		}
		// No comments, add comment
		if (totalCount === 0) {
			return formatMessage(messages.addComment);
		}
		// Less than 100 comments, show count
		if (totalCount < 100) {
			return formatMessage(messages.commentsCount, {
				commentCount: totalCount,
			});
		}
		// More than 99 comments, show 99+
		return formatMessage(messages.moreComments);
	}, [formatMessage, totalCount]);

	const cellContent = (
		<>
			<ContentWrapper noComments={totalCount === 0}>
				<CommentIcon size="medium" label={formatMessage(messages.comments)} />
				{renderedCommentCount}
			</ContentWrapper>
		</>
	);

	if (fg('jira_list_keyboard_nav')) {
		return (
			<Box xcss={wrapperStyles} onClick={handleClick}>
				{cellContent}
			</Box>
		);
	}

	return (
		<CellWrapper
			isFocused={isSelected}
			isEditable
			onClick={handleClick}
			onBlur={onBlur}
			tabIndex={0}
			onFocus={onFocus}
			onKeyDown={handleKeyDown}
			ref={onWrapperRef}
		>
			{cellContent}
		</CellWrapper>
	);
};

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContentWrapper = styled.div<{ noComments: boolean }>({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginRight: token('space.050', '4px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ noComments }) =>
		noComments ? token('color.text.subtlest', N300A) : token('color.text.accent.gray.bolder', N900),
});

const wrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	cursor: 'pointer',
	paddingLeft: 'space.100',
});
